import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { z } from "zod";
import { LIVE_VIEW_SEARCH } from "../shared/app-pages";
import { MAIN_DEFAULT_LOOK } from "../shared/character";
import { Perso } from "./components/avatar";
import { AppShell } from "./components/shell";
import { Button, buttonClass } from "./components/ui";
import { errorText, setUnauthorizedHandler } from "./lib/api";
import { safeInternalPath } from "./lib/format";
import { loadMe, resetSession } from "./lib/session";
import { ActivityPage } from "./routes/activity";
import { AgentPage } from "./routes/agent";
import { AgentDetailPage, AgentsPage } from "./routes/agents";
import { ApprovalsPage } from "./routes/approvals";
import { ChatPage } from "./routes/chat";
import { ComputerPage } from "./routes/computer";
import { ConnectionsPage } from "./routes/connections";
import { LinkPage } from "./routes/link";
import { LoginPage } from "./routes/login";
import { MemoryPage } from "./routes/memory";
import { MissionPage } from "./routes/mission";
import { MissionsPage } from "./routes/missions";
import { WatchesPage } from "./routes/watches";
import { OnboardingPage } from "./routes/onboarding";
import { RunPage } from "./routes/run";
import { ScreenOnlyPage } from "./routes/screen-only";
import { SettingsPage } from "./routes/settings";
import { VaultPage } from "./routes/vault";
import { VaultAddPage } from "./routes/vault-add";
import { pickSiteLang, type SiteLang } from "./site/lang";

/** The public site, in its own chunk: the app never downloads it, a visitor of /decouvrir gets it at once. */
const SitePage = lazy(() => import("./site/site-page").then((m) => ({ default: m.SitePage })));
const SITE_LANG_KEY = "ariston.lang";

function storedSiteLang(): string | undefined {
  try {
    return localStorage.getItem(SITE_LANG_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

// Search params are external input: validated, never trusted. TanStack parses numeric-looking values as numbers.
const text = z.union([z.string(), z.number()]).transform((v) => String(v).slice(0, 500));
const loginSearch = z.object({ next: text.optional().catch(undefined), invitation: text.optional().catch(undefined) });
const siteSearch = z.object({ lang: text.optional().catch(undefined) });
const focusSearch = z.object({ focus: text.optional().catch(undefined) });
const codeSearch = z.object({ code: text.optional().catch(undefined) });
const jetonSearch = z.object({ jeton: text.optional().catch(undefined) });
const appSearch = z.object({ app: text.optional().catch(undefined) });
const screenSearch = z.object({ ecran: text.optional().catch(undefined) });

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
  errorComponent: RootError,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connexion",
  validateSearch: (raw: Record<string, unknown>) => loginSearch.parse(raw),
  beforeLoad: async ({ search }) => {
    const me = await loadMe().catch(() => null);
    if (me) throw redirect({ href: me.onboarded ? (safeInternalPath(search.next) ?? "/") : "/bienvenue", replace: true });
  },
  component: function LoginRoute() {
    const { next, invitation } = loginRoute.useSearch();
    return <LoginPage next={safeInternalPath(next) ?? "/"} invitation={invitation?.trim().slice(0, 100) || undefined} />;
  },
});

/** /decouvrir — the public site of Ariston, in six languages (?lang=fr|en|es|de|it|pt, else the browser's). */
const siteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/decouvrir",
  validateSearch: (raw: Record<string, unknown>) => siteSearch.parse(raw),
  beforeLoad: async () => ({ signedIn: Boolean(await loadMe().catch(() => null)) }),
  component: function SiteRoute() {
    const { lang } = siteRoute.useSearch();
    const { signedIn } = siteRoute.useRouteContext();
    const navigate = useNavigate();
    const chosen = pickSiteLang(lang ?? storedSiteLang());
    const choose = (next: SiteLang) => {
      try {
        localStorage.setItem(SITE_LANG_KEY, next);
      } catch {
        // Private browsing: the choice still lives in the address.
      }
      void navigate({ to: "/decouvrir", search: { lang: next }, replace: true, resetScroll: false });
    };
    return (
      <Suspense fallback={<div style={{ minHeight: "100dvh", background: "#111111" }} />}>
        <SitePage lang={chosen} signedIn={signedIn} onLang={choose} />
      </Suspense>
    );
  },
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/bienvenue",
  beforeLoad: async () => {
    const me = await loadMe();
    if (!me) throw redirect({ to: "/connexion", replace: true });
    if (me.onboarded) throw redirect({ to: "/", replace: true });
    return { me };
  },
  component: function OnboardingRoute() {
    const { me } = onboardingRoute.useRouteContext();
    return <OnboardingPage me={me} />;
  },
});

/** The one-tap vault link the agent sends: no session needed (a write-only, single-use token). */
const vaultAddRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/coffre/ajouter",
  validateSearch: (raw: Record<string, unknown>) => jetonSearch.parse(raw),
  component: function VaultAddRoute() {
    const { jeton } = vaultAddRoute.useSearch();
    return <VaultAddPage token={jeton} />;
  },
});

/** /ecran — the live screen alone, no shell (the native app's web view, or a full window). Same guard as the app. */
const screenOnlyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ecran",
  beforeLoad: async ({ location }) => {
    const me = await loadMe();
    if (!me) throw redirect({ to: "/connexion", search: { next: location.href }, replace: true });
    if (!me.onboarded) throw redirect({ to: "/bienvenue", replace: true });
  },
  component: ScreenOnlyPage,
});
/** Everything behind the session: signed in and onboarded. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ location }) => {
    const me = await loadMe();
    // A visitor who is not signed in and lands on the home page sees the public site; any other page asks to sign in.
    if (!me && location.pathname === "/") throw redirect({ to: "/decouvrir", replace: true });
    if (!me) throw redirect({ to: "/connexion", search: { next: location.href }, replace: true });
    if (!me.onboarded) throw redirect({ to: "/bienvenue", replace: true });
    return { me };
  },
  component: AppShell,
});

/** « / » is the conversation with the main agent (V2: everything starts from it); « /discussion » stays its alias. */
const pulseRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: ChatPage });
const chatRoute = createRoute({ getParentRoute: () => appRoute, path: "/discussion", component: ChatPage });
const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/approbations",
  validateSearch: (raw: Record<string, unknown>) => focusSearch.parse(raw),
  component: function ApprovalsRoute() {
    const { focus } = approvalsRoute.useSearch();
    return <ApprovalsPage focus={focus} />;
  },
});
const missionsRoute = createRoute({ getParentRoute: () => appRoute, path: "/missions", component: MissionsPage });
const missionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/missions/$id",
  component: function MissionRoute() {
    const { id } = missionRoute.useParams();
    return <MissionPage id={id} />;
  },
});
const runRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/runs/$id",
  component: function RunRoute() {
    const { id } = runRoute.useParams();
    return <RunPage id={id} />;
  },
});
const computerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ordinateur",
  validateSearch: (raw: Record<string, unknown>) => screenSearch.parse(raw),
  component: function ComputerRoute() {
    // /ordinateur?ecran=direct (LIVE_VIEW_PATH, the link the agent sends) opens the live screen at once.
    const { ecran } = computerRoute.useSearch();
    return <ComputerPage live={ecran === LIVE_VIEW_SEARCH.ecran} />;
  },
});
const linkRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/link",
  validateSearch: (raw: Record<string, unknown>) => codeSearch.parse(raw),
  component: function LinkRoute() {
    const { code } = linkRoute.useSearch();
    return <LinkPage code={code} />;
  },
});
const memoryRoute = createRoute({ getParentRoute: () => appRoute, path: "/memoire", component: MemoryPage });
const vaultRoute = createRoute({ getParentRoute: () => appRoute, path: "/coffre", component: VaultPage });
const connectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/connexions",
  validateSearch: (raw: Record<string, unknown>) => appSearch.parse(raw),
  component: function ConnectionsRoute() {
    const { app } = connectionsRoute.useSearch();
    return <ConnectionsPage returnedApp={app} />;
  },
});
const settingsRoute = createRoute({ getParentRoute: () => appRoute, path: "/reglages", component: SettingsPage });
const agentRoute = createRoute({ getParentRoute: () => appRoute, path: "/agent", component: AgentPage });
const agentsRoute = createRoute({ getParentRoute: () => appRoute, path: "/agents", component: AgentsPage });
const agentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/agents/$id",
  // The main agent's conversation is « / ».
  beforeLoad: ({ params }) => {
    if (params.id === "main") throw redirect({ to: "/", replace: true });
  },
  component: function AgentDetailRoute() {
    const { id } = agentDetailRoute.useParams();
    return <AgentDetailPage id={id} />;
  },
});
const activityRoute = createRoute({ getParentRoute: () => appRoute, path: "/activite", component: ActivityPage });
/** « Plus » (the phone's former « more » page): its place is now the agent's sheet, opened over the conversation. */
const moreRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plus",
  beforeLoad: () => {
    throw redirect({ to: "/", search: { feuille: "agent" } as never, replace: true });
  },
});
const watchesRoute = createRoute({ getParentRoute: () => appRoute, path: "/veilles", component: WatchesPage });

const routeTree = rootRoute.addChildren([
  loginRoute,
  siteRoute,
  onboardingRoute,
  vaultAddRoute,
  screenOnlyRoute,
  appRoute.addChildren([
    pulseRoute,
    chatRoute,
    approvalsRoute,
    missionsRoute,
    missionRoute,
    runRoute,
    computerRoute,
    linkRoute,
    memoryRoute,
    vaultRoute,
    connectionsRoute,
    settingsRoute,
    agentRoute,
    agentsRoute,
    agentDetailRoute,
    activityRoute,
    moreRoute,
    watchesRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  scrollRestoration: true,
  defaultPendingMs: 400,
  defaultPendingComponent: PendingScreen,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Any 401 from the API (expired or revoked session): forget everything and go back to the login page.
setUnauthorizedHandler(() => {
  resetSession();
  const loc = router.state.location;
  if (loc.pathname === "/connexion") return;
  const next = loc.pathname === "/" || loc.pathname === "/bienvenue" ? undefined : loc.href;
  void router.navigate({ to: "/connexion", search: { next }, replace: true });
});

function PendingScreen() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" role="status" aria-label="Chargement">
      <Perso look={MAIN_DEFAULT_LOOK} size={44} mood="reflechit" />
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <Perso look={MAIN_DEFAULT_LOOK} size={96} mood="reflechit" />
      <h1 className="font-display text-[26px]">Page introuvable</h1>
      <a href="/" className={buttonClass("primary", "md")}>
        Revenir à la conversation
      </a>
    </div>
  );
}

function RootError({ error }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <Perso look={MAIN_DEFAULT_LOOK} size={96} mood="reflechit" />
      <h1 className="font-display text-[26px]">Oups, ça n'a pas marché</h1>
      <p className="max-w-sm text-[14px] text-muted">{errorText(error)}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button onClick={() => void router.invalidate()}>Réessayer</Button>
        <Button variant="secondary" onClick={() => window.location.reload()}>
          Recharger la page
        </Button>
      </div>
    </div>
  );
}
